/**
 * Cấu hình i18n — hai ngôn ngữ, tiếng Việt là gốc (ADR-UI-0010).
 *
 * Ngôn ngữ **không** nằm trên URL: cổng quản trị không có trang công khai cần
 * chia sẻ theo ngôn ngữ, nên một đoạn `/vi/` hay `/en/` chỉ làm mọi liên kết
 * nội bộ dài thêm mà không mua lại được gì. Lựa chọn lưu ở `localStorage` và
 * được áp trước khi vẽ, giống cách chủ đề đang làm (`index.html`).
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { vi } from './messages/vi';
import { en } from './messages/en';

/** Ngôn ngữ chọn được. Thêm ngôn ngữ = thêm một mã ở đây + một tệp messages. */
export const NGON_NGU = ['vi', 'en'] as const;
export type NgonNgu = (typeof NGON_NGU)[number];

export const NGON_NGU_GOC: NgonNgu = 'vi';

/**
 * Nhãn của từng ngôn ngữ **viết bằng chính ngôn ngữ đó**.
 *
 * Không dịch nhãn này sang ngôn ngữ đang hiển thị: người đang mắc kẹt trong một
 * ngôn ngữ họ không đọc được phải tìm thấy ngôn ngữ của mình, mà `Tiếng Anh`
 * thì vô nghĩa với người chỉ đọc được `English`.
 */
export const NHAN_NGON_NGU: Record<NgonNgu, string> = {
  vi: 'Tiếng Việt',
  en: 'English',
};

/** Khóa lưu ngôn ngữ. Phải khớp script chặn nhấp nháy trong `index.html`. */
export const LANG_STORAGE_KEY = 'foxai-lang';

/** Mã `lang` của thẻ `<html>` theo từng ngôn ngữ, cho trình đọc màn hình. */
const MA_HTML_LANG: Record<NgonNgu, string> = {
  vi: 'vi',
  en: 'en',
};

/**
 * Locale truyền cho `Intl` và `dayjs`.
 *
 * `en-GB` chứ không `en-US`: ngày ở đây là `dd/MM/yyyy` theo đặc tả, và
 * `en-US` sẽ đảo thành `MM/dd/yyyy` — một ngày `03/04` đọc sai tháng là lỗi
 * nghiệp vụ, không phải lỗi trình bày. Xem `lib/dinh-dang.ts`.
 */
export const MA_LOCALE: Record<NgonNgu, string> = {
  vi: 'vi-VN',
  en: 'en-GB',
};

export function laNgonNguHopLe(ma: unknown): ma is NgonNgu {
  return typeof ma === 'string' && (NGON_NGU as readonly string[]).includes(ma);
}

/**
 * Đọc ngôn ngữ đã lưu; chưa chọn lần nào thì về tiếng Việt.
 *
 * Không theo `navigator.language`: người dùng là doanh nghiệp Việt nhưng nhiều
 * máy cài trình duyệt tiếng Anh, nên đoán theo trình duyệt làm phần lớn người
 * mới vào thấy tiếng Anh. Ai cần tiếng Anh thì chọn một lần ở menu tài khoản.
 */
export function docNgonNgu(): NgonNgu {
  try {
    const luu = localStorage.getItem(LANG_STORAGE_KEY);
    if (laNgonNguHopLe(luu)) return luu;
  } catch {
    // Chế độ riêng tư hoặc bị chặn lưu: vẫn đổi được, chỉ không nhớ.
  }
  return NGON_NGU_GOC;
}

/** Ghi ngôn ngữ lên `<html lang>` và nhớ lựa chọn. */
export function ghiNgonNgu(ma: NgonNgu): void {
  document.documentElement.lang = MA_HTML_LANG[ma];
  try {
    localStorage.setItem(LANG_STORAGE_KEY, ma);
  } catch {
    // Không nhớ được thì thôi; ngôn ngữ vẫn đổi trong phiên này.
  }
}

/**
 * Khởi tạo i18next.
 *
 * `returnNull: false` để `t()` luôn trả `string` — nhờ vậy chỗ dùng không phải
 * kiểm `null`, và `t()` đặt được thẳng vào `title`, `aria-label`, `placeholder`.
 *
 * `saveMissing` **tắt** nhưng `missingKeyHandler` vẫn bật ở chế độ dev: khoá
 * thiếu không được phép im lặng. Khác next-intl của bản gốc — nó *ném* khi
 * thiếu khoá, làm trắng cả màn hình (§13.2 của spec). Ở đây khoá thiếu hiện ra
 * dưới dạng chính nó và kêu trong console: một nhãn sai chữ còn dùng được, một
 * màn trắng thì không. `scripts/check-i18n.mjs` là chốt chặn thật, chạy lúc CI.
 */
export function khoiTaoI18n(): typeof i18next {
  const banDau = docNgonNgu();

  void i18next.use(initReactI18next).init({
    resources: {
      vi: { translation: vi },
      en: { translation: en },
    },
    lng: banDau,
    fallbackLng: NGON_NGU_GOC,
    returnNull: false,
    // Chuỗi của kho không dùng cú pháp `a.b` làm đường dẫn lồng, và nhiều nhãn
    // nghiệp vụ có dấu `.` ở cuối câu. Tắt để `t('loi.mang')` không bị hiểu sai.
    keySeparator: '.',
    nsSeparator: false,
    interpolation: {
      // React đã chống XSS khi vẽ; escape thêm một lần nữa làm `&` thành `&amp;`.
      escapeValue: false,
    },
    react: {
      // Chuỗi của ta không chứa thẻ HTML, nên không cần `Trans` ở mọi chỗ.
      transSupportBasicHtmlNodes: false,
    },
    ...(import.meta.env.DEV
      ? {
          missingKeyHandler: (
            _lng: readonly string[],
            _ns: string,
            khoa: string,
          ) => {
            console.warn(`[i18n] thiếu khoá: ${khoa}`);
          },
          parseMissingKeyHandler: (khoa: string) => khoa,
        }
      : {}),
  });

  ghiNgonNgu(banDau);
  return i18next;
}

export { i18next };
